import React, { useState, useEffect } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import Markdown from "components/common/Markdown";
import Editor from "components/elements/Editor";
import CommentItem from "components/elements/CommentItem";

import rpc, { buildRequest, signoutAndRedirectToIndex } from "lib/rpc";

export default function ManualScoreDetails(props) {
  const [score, setScore] = useState(props.score);
  const [comment, setComment] = useState();
  const [comments, setComments] = useState([]);

  useEffect(() => {
    rpc
      .post("", buildRequest("admin.assignments.comments.list", { result_id: props.id }))
      .then((response) => {
        if (!response.data.result.message) {
          setComments(response.data.result);
        } else {
          if (response.data.result.code === 5) {
            signoutAndRedirectToIndex();
          }
          console.error(response.data.result);
        }
      })
      .catch((e) => {
        console.error(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setScore(props.score);

    if (!comment) {
      // for the first time quick quotting in the comment
      defaultComment();
    }
  }, [props.score, props.answers, comment]);

  const defaultComment = () => {
    // sets the default comment content as a quote of the original answer for convenience
    setComment("> " + props.answers[0][0].split("\n").join("\n> "));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // admin.assignments.comments.create
    rpc
      .post(
        "",
        buildRequest("admin.assignments.comments.create", {
          assignment_result_id: props.id,
          comment,
          score,
        }),
      )
      .then((response) => {
        if (!response.data.result.message) {
          defaultComment();
          setComments([response.data.result, ...comments]);
        } else {
          if (response.data.result.code === 5) {
            signoutAndRedirectToIndex();
          }
          console.error(response.data.result);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  };

  const deleteComment = (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm("Are you sure you want to delete the comment? This action cannot be undone.")) {
      console.log("Proceed deleting the comment", id);
      rpc
        .post(
          "",
          buildRequest("admin.assignments.comments.delete", {
            id: id,
          }),
        )
        .then((response) => {
          if (!response.data.result.message) {
            setComments([...comments.filter((c) => c.id !== id)]);
          } else {
            if (response.data.result.code === 5) {
              signoutAndRedirectToIndex();
            }
            console.error(response.data.result);
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return false;
    }
  };

  const escapeHtml = (str) => {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const renderAssignment = (item) => {
    const toRender = [];

    toRender.push(
      `<u data-index="0" data-label="0" data-iscorrect="${item.is_correct ? "correct" : "wrong"}" data-answer="${escapeHtml(item.assignment)}" data-correct="${escapeHtml(item.answered)}">${item.assignment}</u>`,
    );

    return (
      <Markdown
        children={toRender.join(" ")}
        components={{
          u: ({ node, ...props }) => (
            <>
              <span className={`score-details__answer ${props["data-iscorrect"]}`}>{props["data-answer"]}</span>
              <blockquote className="score-details__comment">{props["data-correct"]}</blockquote>
            </>
          ),
        }}
      />
    );
  };

  try {
    return (
      <Stack spacing={2}>
        <Typography variant="h5">AI evaluation:</Typography>
        <div className="score">
          <div className="score-details">
            {props.score_details.map((elem, elemIndex) => (
              <div key={`row_${elemIndex}`} className="score-details__row">
                <div className="score-details__question">{renderAssignment(elem)}</div>
              </div>
            ))}
          </div>
        </div>
        <Stack spacing={2}>
          <Typography variant="h5">Teacher's comment(s):</Typography>
          {comments ? comments.map((c) => <CommentItem onDelete={deleteComment} {...c} />) : "No comments yet"}
        </Stack>
        <Box>
          <Stack spacing={1}>
            <Typography variant="h5">Teacher's evaluation:</Typography>
            <OutlinedInput
              label="Score"
              error={isNaN(score) || score < 2 || score > 100}
              helperText={isNaN(score) || score < 2 || score > 100 ? "Score must be between 2 and 100" : ""}
              value={score}
              sx={{ m: 1, width: "25ch" }}
              endAdornment={<InputAdornment position="end">%</InputAdornment>}
              onChange={(e) => setScore(parseInt(e.target.value, 10))}
            />
            <Editor defaultValue={comment || ""} value={comment} setValue={setComment} />
            <Button
              disabled={score < 3 || !comment || comment.length < 10}
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Stack>
        </Box>
      </Stack>
    );
  } catch (e) {
    return (
      <div>
        <p>Results cannot be rendered. Please report it to the admins via Slack</p>
        <pre>{e}</pre>
      </div>
    );
  }
}
